import ClaimsService from "@/api/services/claims";
import { toSnakeCase, toCamelCase } from "@estaly/ui";

const initialState = {
  currentClaim: null,
};

export default {
  namespaced: true,

  state: initialState,

  getters: {
    isActiveClaim: () => (claim) => {
      const closedStatuses = ["resolved", "cancelled", "refused"];
      return !closedStatuses.includes(claim?.status?.toLowerCase());
    },

    anyPaymentRequired: (state, getters) => {
      return (
        getters.deductiblePaymentRequired ||
        getters.finalSettlementPaymentRequired ||
        getters.finalSettlementAndDeductiblePaymentRequired
      );
    },

    deductiblePaymentRequired: (state) => {
      return state.currentClaim?.status === "pending_deductible_payment";
    },

    finalSettlementPaymentRequired: (state) => {
      return state.currentClaim?.status === "pending_final_settlement";
    },

    finalSettlementAndDeductiblePaymentRequired: (state) => {
      return state.currentClaim?.status === "pending_deductible_payment_and_final_settlement";
    },

    declarationUpdateRequired: (state) => {
      return state.currentClaim?.status === "pending_additional_info";
    },

    customerIbanRequired: (state) => {
      return state.currentClaim?.status === "waiting_for_customer_iban";
    },

    claimWaitingForQuotation: (state) => {
      return state.currentClaim?.status === "waiting_for_quotation";
    },

    claimWaitingForInvoice: (state) => {
      return state.currentClaim?.status === "waiting_for_invoice";
    },

    externalMerchantClaimHandler: (state) => {
      return state.currentClaim?.claimHandler === "external_merchant";
    },

    externalMerchantQuotationRequired: (state, getters) => {
      return getters.externalMerchantClaimHandler && getters.claimWaitingForQuotation;
    },

    externalMerchantInvoiceRequired: (state, getters) => {
      return getters.externalMerchantClaimHandler && getters.claimWaitingForInvoice;
    },

    anyActionRequired: (state, getters) => {
      return (
        getters.declarationUpdateRequired ||
        getters.anyPaymentRequired ||
        getters.customerIbanRequired ||
        getters.externalMerchantQuotationRequired ||
        getters.externalMerchantInvoiceRequired ||
        getters.deductiblePaymentRequired ||
        getters.finalSettlementPaymentRequired ||
        getters.finalSettlementAndDeductiblePaymentRequired
      );
    },

    hasRequiredAction: () => (claim) => {
      const status = claim.status;
      const claimHandler = claim.claimHandler;

      return (
        status === "pending_additional_info" ||
        status === "waiting_for_customer_iban" ||
        status === "pending_additional_info" ||
        status === "pending_deductible_payment" ||
        status === "pending_final_settlement" ||
        status === "pending_deductible_payment_and_final_settlement" ||
        (claimHandler === "external_merchant" &&
          (status === "waiting_for_quotation" || status === "waiting_for_invoice"))
      );
    },

    statusStyle: () => (status) => {
      switch (status) {
        // Statuts rouges (refus et annulations)
        case "cancelled":
        case "refused":
        case "quotation_refused":
        case "invoice_refused":
          return "bg-red-100 text-red-800";

        // Statuts orange (en attente d'approbation)
        case "to_be_approved":
        case "quotation_to_be_approved":
        case "invoice_to_be_approved":
          return "bg-indigo-100 text-indigo-800";

        // Statuts bleus (en cours de traitement/attente)
        case "processing":
        case "pending_deductible_payment":
        case "pending_deductible_payment_and_final_settlement":
        case "pending_final_settlement":
        case "waiting_for_quotation":
        case "waiting_for_customer_quotation":
        case "waiting_for_invoice":
        case "waiting_for_clients_bank_details":
        case "waiting_for_resolution":
        case "waiting_for_customer_iban":
        case "waiting_for_return_appointment":
        case "pickup_scheduled":
          return "bg-blue-100 text-blue-800";

        // Statuts verts (approuvés/résolus)
        case "resolved":
        case "approved":
        case "clients_bank_details_uploaded":
        case "voucher_sent":
        case "pickup_completed":
        case "quotation_approved":
        case "invoice_approved":
          return "bg-green-100 text-green-800";

        // Statut jaune
        case "pending":
          return "bg-yellow-100 text-yellow-800";

        // Statut gris
        case "pending_additional_info":
          return "bg-gray-100 text-gray-800";

        // Statut par défaut
        default:
          return "bg-gray-100 text-gray-800";
      }
    },

    claimPickupScheduled: (state) => {
      return state.currentClaim?.status === "pickup_scheduled";
    },

    replacementHandlingType: (state) => {
      return state.currentClaim?.handlingType === "replacement";
    },
  },

  mutations: {
    setCurrentClaim(state, currentClaim) {
      state.currentClaim = currentClaim;
    },
  },

  actions: {
    async fetchIndex(_, { page, planSaleFriendlyId, includes }) {
      return await ClaimsService.fetchIndex({ page, planSaleFriendlyId, includes });
    },

    async fetch({ commit, dispatch }, claimFriendlyId) {
      const response = await ClaimsService.fetch(claimFriendlyId, [
        "claim_declarations",
        "final_settlement",
        "claim_deductible",
        "claim_product",
        "claim_historical_datas",
        "quotation",
        "invoice",
        "optional_data",
        "voucher",
      ]);
      if (!response?.success) {
        dispatch("resetCurrentClaim");
        return response;
      }

      const claim = toCamelCase(response.data);
      commit("setCurrentClaim", claim);
      return response;
    },

    getPaymentStripeUrl(_, claimId) {
      return ClaimsService.getPaymentStripeUrl(claimId);
    },

    resetCurrentClaim({ commit }) {
      commit("setCurrentClaim", null);
    },

    async uploadQuotation({ state, dispatch }, { costItems, file, productState }) {
      let formData = new FormData();
      formData.append("file", file);

      costItems
        .map((item) => toSnakeCase(item))
        .forEach((item, index) => {
          Object.keys(item).forEach((key) => {
            formData.append(`cost_items[${index}][${key}]`, item[key]);
          });
        });

      if (productState) {
        formData.append("product_state", productState);
      }

      const response = await ClaimsService.uploadQuotation(state.currentClaim.id, formData);

      if (!response?.success) return response;

      dispatch("fetch", state.currentClaim.friendlyId);

      return response;
    },

    declareIrreparable: async function ({ state, dispatch }, { message, reason, irreparabilityCertificate }) {
      let formData = new FormData();
      if (irreparabilityCertificate) formData.append("irreparability_certificate", irreparabilityCertificate);
      formData.append("message", message);
      formData.append("reason", reason);

      const response = await ClaimsService.declareIrreparable(state.currentClaim.id, formData);

      if (!response?.success) return response;

      return dispatch("fetch", state.currentClaim.friendlyId);
    },

    async extractCostItems({ state }, { file }) {
      if (!state.currentClaim?.id) {
        return { success: false, error: "missing_current_claim_id" };
      }
      return await ClaimsService.extractCostItems(state.currentClaim.id, file);
    },

    async uploadInvoice({ state, dispatch }, { costItems, file }) {
      let formData = new FormData();
      formData.append("file", file);

      costItems
        .map((item) => toSnakeCase(item))
        .forEach((item, index) => {
          Object.keys(item).forEach((key) => {
            formData.append(`cost_items[${index}][${key}]`, item[key]);
          });
        });

      const response = await ClaimsService.uploadInvoice(state.currentClaim.id, formData);

      if (!response?.success) return response;

      dispatch("fetch", state.currentClaim.friendlyId);

      return response;
    },
  },
};
